import React, { useEffect, useState } from 'react';
import client from '../api/client';
import styles from './CommonPage.module.css';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

interface FileTransferLog {
  id: number;
  userId?: string;
  username?: string;
  peerId: string;
  direction: string;
  totalSize: number;
  transferredSize: number;
  status: string;
  errorMessage?: string;
  createdAt: string;
}

const FileTransferLogPage: React.FC = () => {
  const [logs, setLogs] = useState<FileTransferLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [keyword, setKeyword] = useState('');

  const fetchLogs = async (pageNumber: number) => {
    try {
      setLoading(true);
      let url = `/admin/file-logs?page=${pageNumber}&size=20&sort=createdAt,desc`;
      if (keyword) {
        url += `&username=${encodeURIComponent(keyword)}`;
      }
      
      const response = await client.get(url);
      setLogs(response.data.content);
      setTotalPages(response.data.totalPages);
      setPage(pageNumber);
    } catch (error) {
      console.error('Failed to fetch file logs', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(0);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchLogs(0);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handlePrev = () => {
    if (page > 0) fetchLogs(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) fetchLogs(page + 1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.wrapper}>
            <h1>파일 전송 로그</h1>
            <p>원격 지원 중 발생한 파일 전송 이력을 조회합니다.</p>
        </div>
      </div>

       <div className={styles.card} style={{ marginBottom: '24px' }}>
          <div className={styles.cardAboveBox} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
                <div className={styles.inputBox} style={{ position: 'relative', width: '300px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input 
                        style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                        placeholder="사용자 ID 검색..."
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                </div>
                <button type="submit" className={styles.primaryBtn}>검색</button>
            </form>
          </div>
      </div>
      
      <div className={styles.tableContainer}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center' }}>로딩 중...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>시간</th>
                <th>사용자</th>
                <th>접속 번호</th>
                <th>방향</th>
                <th>전체 크기</th>
                <th>전송된 크기</th>
                <th>상태</th>
                <th>에러 메시지</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td data-label="시간">{formatDate(log.createdAt)}</td>
                  <td data-label="사용자">{log.username} ({log.userId})</td>
                  <td data-label="접속 번호">{log.peerId}</td>
                  <td data-label="방향">
                    <span style={{ 
                        color: log.direction === 'SEND' ? '#2563eb' : '#059669',
                        fontWeight: 600
                    }}>
                      {log.direction === 'SEND' ? '보냄' : '받음'}
                    </span>
                  </td>
                  <td data-label="전체 크기">{formatFileSize(log.totalSize)}</td>
                  <td data-label="전송된 크기">{formatFileSize(log.transferredSize)}</td>
                  <td data-label="상태">
                    <span style={{ 
                        padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: log.status === 'COMPLETED' ? '#dcfce7' : log.status === 'ERROR' ? '#fee2e2' : '#fef3c7',
                        color: log.status === 'COMPLETED' ? '#166534' : log.status === 'ERROR' ? '#991b1b' : '#92400e',
                        display: 'inline-block'
                    }}>
                      {log.status}
                    </span>
                  </td>
                  <td data-label="에러 메시지" style={{ color: '#ef4444' }}>{log.errorMessage}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.pagination}>
          <button onClick={handlePrev} disabled={page === 0}>
              <ChevronLeft size={16} /> 이전
          </button>
          <span>
              {page + 1} / {totalPages === 0 ? 1 : totalPages} 페이지
          </span>
          <button onClick={handleNext} disabled={page >= totalPages - 1}>
              다음 <ChevronRight size={16} />
          </button>
      </div>
    </div>
  );
};

export default FileTransferLogPage;
